import { forwardRef, useMemo } from 'react';
import Dropzone, {
  type Accept,
  type DropzoneOptions,
  type DropzoneRef,
} from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { MimeType } from '@repo/shared';
import { Upload } from 'lucide-react';

import { cn } from '@/lib/utils';

type Keys = keyof typeof MimeType;
type Values = (typeof MimeType)[Keys];

interface FileInputProps extends Omit<DropzoneOptions, 'onDrop'> {
  mimeTypes: Array<Values>;
  onChange: DropzoneOptions['onDrop'];
}

export const FileInput = forwardRef<DropzoneRef, FileInputProps>(
  ({ mimeTypes, onChange, ...props }, ref) => {
    const { t } = useTranslation();
    const keys = useMemo(() => Object.keys(MimeType), []);

    const accept: Accept = useMemo(
      () =>
        Object.values(mimeTypes).reduce((acc, mimeType) => {
          const key = keys.find((_key) => MimeType[_key as Keys] === mimeType);
          if (!key) {
            return acc;
          }
          return { ...acc, [`${mimeType}`]: [`.${key.toLowerCase()}`] };
        }, {}),
      [mimeTypes, keys]
    );

    return (
      <Dropzone
        ref={ref}
        onDrop={onChange}
        accept={accept}
        {...props}>
        {({ getRootProps, getInputProps }) => (
          <section
            className={cn(
              'border-accent-gray hover:bg-accent hover:text-accent-foreground flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border px-16 py-12',
              {
                'cursor-not-allowed bg-gray-50 opacity-50': props.disabled,
              }
            )}
            {...getRootProps()}>
            <Upload className='text-primary-darker h-6 w-6' />
            <input {...getInputProps()} />
            <span className='text-primary-darker text-xs font-semibold'>
              {t('components.fileInput.clickOrDrop')}
            </span>
            <span className='text-accent-gray text-xs font-semibold'>
              {t('components.fileInput.possibleFormats', {
                formats: Object.values(accept).join(' - '),
                maxSize: props.maxSize ?? '-',
              })}
            </span>
          </section>
        )}
      </Dropzone>
    );
  }
);
FileInput.displayName = 'FileInput';
